import { useState, useRef, useEffect } from 'react';
import MistralClient from '@mistralai/mistralai';
import { MessageSquare, Send, Bot, Loader2, X, Minimize2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { generateSystemPrompt } from '../config/chatbot-prompt';
import { useAuth } from '../contexts/AuthContext';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AppData {
  transactions: any[];
  agencies: any[];
  negotiators: any[];
  assistants: any[];
}

export default function ChatBot() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(() => {
    const saved = localStorage.getItem('chatbot_visible');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appData, setAppData] = useState<AppData | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<MistralClient | null>(null);

  // Listen for visibility changes from settings
  useEffect(() => {
    const handleVisibilityChange = (event: CustomEvent) => {
      setIsVisible(event.detail.visible);
    };

    window.addEventListener('chatbot-visibility-changed', handleVisibilityChange as EventListener);
    
    return () => {
      window.removeEventListener('chatbot-visibility-changed', handleVisibilityChange as EventListener);
    };
  }, []);

  // Initialize Mistral client
  useEffect(() => {
    const MISTRAL_API_KEY = import.meta.env.VITE_MISTRAL_API_KEY;
    if (!clientRef.current && MISTRAL_API_KEY) {
      try {
        clientRef.current = new MistralClient(MISTRAL_API_KEY);
      } catch (err) {
        console.error('Error initializing Mistral client:', err);
        setError('Erreur d\'initialisation du service. Veuillez rafraîchir la page.');
      }
    }
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Load application data with improved error handling
  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const fetchWithRetry = async (query: any, retries = 3) => {
        for (let i = 0; i < retries; i++) {
          try {
            const result = await query;
            if (result.error) throw result.error;
            return result.data;
          } catch (err: any) {
            if (i === retries - 1) throw err;
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      };

      const [transactions, agencies, negotiators, assistants] = await Promise.all([
        fetchWithRetry(supabase
          .from('transactions')
          .select(`
            id,
            property_number,
            status,
            mandate_type,
            seller,
            buyer,
            sale_price,
            fees_with_tax,
            fees_without_tax,
            compromise_date,
            expected_acte_date,
            acte_date,
            invoice_date,
            invoice_number,
            encaissement_date,
            transaction_agencies (
              agency
            ),
            transaction_negotiators (
              negotiator:negotiators (
                id,
                firstname,
                lastname
              ),
              role,
              percentage
            ),
            transaction_assistants (
              assistant:assistants (
                id,
                firstname,
                lastname
              ),
              role
            ),
            transaction_retrocessions (
              type,
              amount,
              payment_date
            )
          `)),
        fetchWithRetry(supabase
          .from('agencies')
          .select('*')
          .eq('active', true)),
        fetchWithRetry(supabase
          .from('negotiators')
          .select(`
            id,
            firstname,
            lastname,
            role,
            status,
            negotiator_agencies (
              agency
            )
          `)),
        fetchWithRetry(supabase
          .from('assistants')
          .select(`
            id,
            firstname,
            lastname,
            assistant_agencies (
              agency
            )
          `))
      ]);

      setAppData({
        transactions: transactions || [],
        agencies: agencies || [],
        negotiators: negotiators || [],
        assistants: assistants || []
      });
    } catch (err: any) {
      console.error('Error loading app data:', err);
      setError('Erreur de chargement des données. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !clientRef.current || !appData) return;

    try {
      setLoading(true);
      setError('');

      // Add user message
      const userMessage: Message = { role: 'user', content: input };
      setMessages(prev => [...prev, userMessage]);
      setInput('');

      // Prepare system message
      const systemPrompt = generateSystemPrompt(appData);

      // Prepare messages array for API
      const apiMessages = [
        { role: 'system', content: systemPrompt },
        ...messages.map(m => ({ 
          role: m.role === 'user' ? 'user' : 'assistant', 
          content: m.content 
        })),
        { role: 'user', content: userMessage.content }
      ];

      // Send message to Mistral
      const response = await clientRef.current.chat({
        messages: apiMessages,
        model: 'mistral-medium',
        temperature: 0.7,
        max_tokens: 1000
      });

      // Add assistant response
      if (response.choices?.[0]?.message?.content) {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: response.choices[0].message.content }
        ]);
      } else {
        throw new Error('Réponse invalide de l\'API');
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.message || 'Une erreur est survenue lors de la communication avec l\'API');
      
      // Add error message to chat
      setMessages(prev => [
        ...prev,
        { 
          role: 'assistant', 
          content: 'Désolé, une erreur est survenue. Veuillez réessayer votre question.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Return null if user is not admin or not logged in
  if (!user || user.role !== 'admin' || !isVisible) {
    return null;
  }

  // ChatBot UI
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className={`fixed ${isMinimized ? 'bottom-4 right-4 w-auto' : 'bottom-0 right-0 w-96'} transition-all duration-200 z-50`}>
      {isMinimized ? (
        <button
          onClick={toggleMinimize}
          className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
        >
          <Bot className="w-6 h-6" />
        </button>
      ) : (
        <div className="bg-white rounded-t-lg shadow-xl border border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <Bot className="w-5 h-5 text-blue-600" />
              <h2 className="font-medium">K-Bot</h2>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleMinimize}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <Minimize2 size={18} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div 
            ref={chatRef}
            className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 p-3 rounded-lg">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t bg-white">
            {error && (
              <div className="mb-2 text-sm text-red-500">
                {error}
              </div>
            )}
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="p-2 text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                <Send size={20} />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}